/**
 * ChallengeLanding
 *
 * Public, unauthenticated page for accepting Head-to-Head prediction challenges.
 * Accessible at /challenge/:shortCode
 *
 * Shows:
 *  - Challenger name and their prediction
 *  - Market title and the option they chose
 *  - ZAR challenge amount
 *  - "Accept Challenge" CTA → redirects to the market with option pre-selected
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Swords, TrendingUp, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';

interface ChallengeData {
  id: string;
  short_code: string;
  status: string;
  amount: number;
  message: string | null;
  expires_at: string | null;
  created_at: string;
  market_id: string;
  option_id: number;
  // joined
  market_title: string;
  market_category: string;
  market_status: string;
  option_name: string;
  challenger_email: string | null;
  challenger_name: string | null;
}

export function ChallengeLanding() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenge, setChallenge] = useState<ChallengeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shortCode) return;
    const fetchChallenge = async () => {
      setLoading(true);
      try {
        const { data, error: err } = await supabase
          .from('challenges')
          .select(`
            id, short_code, status, amount, message, expires_at, created_at,
            market_id, option_id,
            markets!challenges_market_id_fkey ( title, category, status ),
            market_options!challenges_option_id_fkey ( name ),
            users!challenges_challenger_id_fkey ( email, user_metadata )
          `)
          .eq('short_code', shortCode.toUpperCase())
          .single();

        if (err || !data) {
          setError('Challenge not found or has expired.');
          return;
        }

        const mkt = Array.isArray(data.markets) ? data.markets[0] : data.markets;
        const opt = Array.isArray(data.market_options) ? data.market_options[0] : data.market_options;
        const usr = Array.isArray(data.users) ? data.users[0] : data.users;

        setChallenge({
          id: data.id,
          short_code: data.short_code,
          status: data.status,
          amount: data.amount,
          message: data.message,
          expires_at: data.expires_at,
          created_at: data.created_at,
          market_id: data.market_id,
          option_id: data.option_id,
          market_title: mkt?.title ?? 'Unknown Market',
          market_category: mkt?.category ?? '',
          market_status: mkt?.status ?? '',
          option_name: opt?.name ?? 'Unknown Option',
          challenger_email: usr?.email ?? null,
          challenger_name: (usr?.user_metadata as Record<string, string> | null)?.full_name
            ?? usr?.email?.split('@')[0]
            ?? 'Someone',
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load challenge');
      } finally {
        setLoading(false);
      }
    };

    fetchChallenge();
  }, [shortCode]);

  const handleAccept = () => {
    if (!challenge) return;
    if (!user) {
      navigate('/login', {
        state: { from: `/challenge/${shortCode}`, afterLoginRedirect: `/markets/${challenge.market_id}?option=${challenge.option_id}` }
      });
      return;
    }
    navigate(`/markets/${challenge.market_id}?option=${challenge.option_id}&challenge=${challenge.id}`);
  };

  const isExpired = challenge?.expires_at ? new Date(challenge.expires_at) < new Date() : false;
  const isOpen = challenge?.status === 'pending' && !isExpired && challenge.market_status === 'active';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Challenge Not Found</h1>
          <p className="text-gray-500 mb-6">{error ?? 'This challenge link is invalid or has expired.'}</p>
          <Link to="/markets" className="px-6 py-3 bg-wayja-teal text-white rounded-xl font-medium hover:bg-opacity-90 transition-colors">
            Browse Markets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4 py-12">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-wayja-teal to-teal-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Swords className="w-6 h-6" />
            <span className="text-sm font-semibold uppercase tracking-wide">Head-to-Head Challenge</span>
          </div>
          <h1 className="text-2xl font-bold">{challenge.challenger_name} challenges you!</h1>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Market */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">{challenge.market_category}</p>
            <h2 className="text-lg font-bold text-gray-900">{challenge.market_title}</h2>
          </div>

          {/* Challenger's pick */}
          <div className="flex items-center gap-3 p-4 bg-teal-50 border border-teal-200 rounded-xl">
            <TrendingUp className="w-5 h-5 text-teal-600 shrink-0" />
            <div>
              <p className="text-xs text-teal-600 font-medium">Their prediction</p>
              <p className="text-base font-bold text-teal-800">{challenge.option_name}</p>
            </div>
          </div>

          {/* Amount */}
          <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
            <span className="text-gray-600 font-medium">Challenge Amount</span>
            <span className="text-2xl font-bold text-gray-900">R{Number(challenge.amount).toFixed(2)}</span>
          </div>

          {/* Message */}
          {challenge.message && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-sm text-yellow-800 italic">"{challenge.message}"</p>
            </div>
          )}

          {/* Expiry */}
          {challenge.expires_at && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>Expires: {new Date(challenge.expires_at).toLocaleString()}</span>
            </div>
          )}

          {/* Status banner */}
          {!isOpen && (
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              challenge.status === 'accepted' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {challenge.status === 'accepted'
                ? <CheckCircle className="w-5 h-5" />
                : <XCircle className="w-5 h-5" />
              }
              <span className="font-medium capitalize">
                {isExpired ? 'This challenge has expired' : `Challenge is ${challenge.status}`}
              </span>
            </div>
          )}

          {/* CTA */}
          {isOpen ? (
            <button
              onClick={handleAccept}
              className="w-full py-4 bg-wayja-teal text-white rounded-xl font-bold text-lg hover:bg-opacity-90 transition-colors active:scale-95"
            >
              Accept Challenge
            </button>
          ) : (
            <Link
              to={`/markets/${challenge.market_id}`}
              className="block w-full py-3 bg-gray-200 text-gray-700 rounded-xl font-medium text-center hover:bg-gray-300 transition-colors"
            >
              View Market
            </Link>
          )}

          <p className="text-xs text-center text-gray-400">
            Powered by <a href="https://wayja.me" className="text-wayja-teal hover:underline">Wayja</a> · Prediction Markets
          </p>
        </div>
      </div>
    </div>
  );
}
